<script setup lang="ts">
import { computed, ref } from 'vue';

import type { FormSchema, VFormRef } from '@modules/forms/components/VForm/types';
import type { IWellKnownDomainsList, IWellKnownLinksForm } from '@modules/well-known-links/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import { TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, WELL_KNOWN_LINKS_PERMISSIONS } from '@modules/well-known-links/constants';
import { useSaveWellKnownLinksMutation, useUpdateDomainsMutation } from '@modules/well-known-links/mutations';
import { useDomainsListQuery, useWellKnownLinksListQuery } from '@modules/well-known-links/queries';
import { assert, isNumber } from '@utils/guards';

const formCreateRef = ref<VFormRef>();
const formUpdateRef = ref<VFormRef>();
const dialogVisible = ref(false);
const currentTableRecordId = ref<number>();

const { hasPermission } = usePermissionsStore();
const { getTranslation } = useI18n();

const showPermission = computed<boolean>(() => hasPermission(WELL_KNOWN_LINKS_PERMISSIONS.SHOW).value);
const editPermission = computed<boolean>(() => hasPermission(WELL_KNOWN_LINKS_PERMISSIONS.EDIT).value);

const { data: domainsList, isPending: isDomainsListPending } = useDomainsListQuery(editPermission);

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useWellKnownLinksListQuery, showPermission);

const {
  mutateAsync: createSubmit,
  isPending: isCreatePending,
  errors: createErrors,
  clearError: createClearError,
} = useSaveWellKnownLinksMutation();

const {
  mutateAsync: updateSubmit,
  isPending: isUpdatePending,
  errors: updateErrors,
  clearError: updateClearErrors,
} = useUpdateDomainsMutation();

const domainsOptions = computed<IWellKnownDomainsList>(() => domainsList.value ?? []);

const schemaForCreate = computed<FormSchema<IWellKnownLinksForm, never, {
  domain_ids: {
    selectOptionValue: 'id';
    selectOptionLabel: 'value';
  };
}>>(() => ({
  package_name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'FORM.PACKAGE_NAME').value,
    required: true,
  },
  sha256_cert_fingerprints: {
    type: EFormInputTypes.TEXTAREA,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'FORM.CERT_FINGERPRINTS').value,
    required: true,
  },
  domain_ids: {
    type: EFormInputTypes.MULTISELECT_BOX,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'FORM.DOMAINS').value,
    options: domainsOptions.value,
    optionValue: 'id',
    optionLabel: 'value',
    required: true,
    isLoading: isDomainsListPending.value,
  },
}));

const schemaForUpdate = computed<FormSchema<IWellKnownLinksForm, never, {
  domain_ids: {
    selectOptionValue: 'id';
    selectOptionLabel: 'value';
  };
}>>(() => ({
  domain_ids: schemaForCreate.value.domain_ids,
}));

async function onCreateSubmit(values: IWellKnownLinksForm): Promise<void> {
  try {
    await createSubmit({ click_domains_well_known: values });
    formCreateRef.value?.reset();
  } catch {}
}

async function onUpdateSubmit(values: IWellKnownLinksForm): Promise<void> {
  try {
    assert(isNumber(currentTableRecordId.value));
    await updateSubmit({ id: currentTableRecordId.value, click_domains_well_known: values });
    formUpdateRef.value?.reset();
    dialogVisible.value = false;
  } catch {}
}

function openUpdateDomainsDialog(id: number) {
  currentTableRecordId.value = id;
  dialogVisible.value = true;
}

function onHideConfirmDialog() {
  currentTableRecordId.value = undefined;
  dialogVisible.value = false;
}
</script>

<template>
  <VCard v-data-qa-auto-id="'WellKnownLinksPage'"
    v-if="editPermission"
    type="form"
    class="mb-4"
    :title="$getTranslation(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'FORM.HEADER')"
  >
    <VForm
      ref="formCreateRef"
      :schema="schemaForCreate"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'FORM.SUBMIT')"
      :custom-errors="createErrors"
      :is-loading="isCreatePending"
      @interacted="createClearError"
      @submit="onCreateSubmit"
    />
  </VCard>

  <VCard
    v-if="showPermission"
    type="table"
  >
    <VTable
      :data="data"
      :title-t-path="$getTPath(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'TABLE.HEADER')"
      :loading="isLoading"
      :is-fetching="isFetching"
      :state="tableState"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="editPermission"
          icon="pi-file-edit"
          @click="openUpdateDomainsDialog(listItem.id)"
        />
      </template>
    </VTable>
  </VCard>

  <VDialog
    :visible="dialogVisible"
    :title="$getTranslation(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'UPDATE.TITLE')"
    @hide="onHideConfirmDialog"
  >
    <VForm
      ref="formUpdateRef"
      :schema="schemaForUpdate"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_ADMIN_WELL_KNOWN_LINKS, 'UPDATE.SUBMIT')"
      :custom-errors="updateErrors"
      :is-loading="isUpdatePending"
      @interacted="updateClearErrors"
      @submit="onUpdateSubmit"
    />
  </VDialog>
</template>
